import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { EMBEDS } from "../i18n/content";

const SITE_URL = "https://shaktilovemusic.com";
const OG_IMAGE = `${SITE_URL}/social-preview.jpg`;

const SEO = {
  "/": {
    en: { title: "Shakti Love Music | DEEJAY in Tenerife for Sunset Sessions & Events", description: "Shakti Love Music is a Tenerife DEEJAY for sunset sessions, weddings, private events, beach clubs and festivals. Discover warm, organic house and book your event." },
    es: { title: "Shakti Love Music | DEEJAY en Tenerife para eventos y atardeceres", description: "Shakti Love Music es una DEEJAY en Tenerife para sesiones al atardecer, bodas, eventos privados, beach clubs y festivales. Descubre su house orgánico y reserva." },
  },
  "/about": {
    en: { title: "About Shakti Love Music | Organic House DEEJAY in Tenerife", description: "Meet Shakti Love Music, a Tenerife DEEJAY creating warm organic house and downtempo journeys for meaningful gatherings." },
    es: { title: "Sobre Shakti Love Music | DEEJAY de house orgánico en Tenerife", description: "Conoce a Shakti Love Music, DEEJAY en Tenerife que crea viajes de house orgánico y downtempo para encuentros memorables." },
  },
  "/music": {
    en: { title: "Music & DEEJAY Mixes | Shakti Love Music", description: "Listen to organic house, downtempo and sunset DEEJAY mixes by Shakti Love Music." },
    es: { title: "Música y mixes DEEJAY | Shakti Love Music", description: "Escucha mixes de house orgánico, downtempo y atardecer de Shakti Love Music." },
  },
  "/services": {
    en: { title: "DEEJAY Services in Tenerife | Weddings, Events & Beach Clubs", description: "Explore DEEJAY services by Shakti Love Music for weddings, private parties, beach clubs, poolside sessions and festivals in Tenerife." },
    es: { title: "Servicios DEEJAY en Tenerife | Bodas, eventos y beach clubs", description: "Descubre los servicios DEEJAY de Shakti Love Music para bodas, fiestas privadas, beach clubs, piscina y festivales en Tenerife." },
  },
  "/gallery": {
    en: { title: "DEEJAY Gallery | Shakti Love Music", description: "Explore moments from Shakti Love Music's sunset sessions, celebrations and live DEEJAY experiences." },
    es: { title: "Galería DEEJAY | Shakti Love Music", description: "Descubre momentos de las sesiones al atardecer, celebraciones y experiencias DEEJAY de Shakti Love Music." },
  },
  "/events": {
    en: { title: "DEEJAY Events in Tenerife | Shakti Love Music", description: "Find upcoming DEEJAY sessions and live appearances by Shakti Love Music in Tenerife." },
    es: { title: "Eventos DEEJAY en Tenerife | Shakti Love Music", description: "Encuentra las próximas sesiones y actuaciones en directo de Shakti Love Music en Tenerife." },
  },
  "/events/past": {
    en: { title: "Past DEEJAY Sessions in Tenerife | Shakti Love Music", description: "Explore the monthly archive of past DEEJAY sessions and live appearances by Shakti Love Music in Tenerife." },
    es: { title: "Sesiones DEEJAY pasadas en Tenerife | Shakti Love Music", description: "Explora el archivo mensual de sesiones DEEJAY y actuaciones pasadas de Shakti Love Music en Tenerife." },
  },
  "/contact": {
    en: { title: "Book Shakti Love Music | DEEJAY for Events in Tenerife", description: "Book Shakti Love Music for your wedding, private event, beach club, lounge or festival in Tenerife." },
    es: { title: "Reserva Shakti Love Music | DEEJAY para eventos en Tenerife", description: "Reserva a Shakti Love Music para tu boda, evento privado, beach club, lounge o festival en Tenerife." },
  },
};

function setMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    if (hreflang) element.setAttribute("hreflang", hreflang);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function basePath(pathname) {
  if (pathname === "/es") return "/";
  return pathname.startsWith("/es/") ? pathname.slice(3) : pathname;
}

function localePath(path, lang) {
  if (lang === "es") return path === "/" ? "/es" : `/es${path}`;
  return path;
}

export function Seo() {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const path = basePath(pathname) || "/";
  const page = SEO[path] || SEO["/"];
  const metadata = page[lang];
  const canonical = `${SITE_URL}${localePath(path, lang)}`;

  useEffect(() => {
    document.title = metadata.title;
    setMeta("name", "description", metadata.description);
    setMeta("name", "robots", "index, follow, max-image-preview:large");
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Shakti Love Music");
    setMeta("property", "og:title", metadata.title);
    setMeta("property", "og:description", metadata.description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("property", "og:image:alt", "Shakti Love Music DEEJAY at sunset in Tenerife");
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:locale", lang === "es" ? "es_ES" : "en_GB");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", metadata.title);
    setMeta("name", "twitter:description", metadata.description);
    setMeta("name", "twitter:image", OG_IMAGE);
    setLink("canonical", canonical);
    setLink("alternate", `${SITE_URL}${localePath(path, "en")}`, "en");
    setLink("alternate", `${SITE_URL}${localePath(path, "es")}`, "es");
    setLink("alternate", `${SITE_URL}${localePath(path, "en")}`, "x-default");

    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Person",
          "@id": `${SITE_URL}/#shakti-love-music`,
          name: "Shakti Love Music",
          url: SITE_URL,
          image: OG_IMAGE,
          description: "DEEJAY in Tenerife for sunset sessions, private events, weddings, beach clubs and festivals.",
          jobTitle: "DEEJAY",
          email: "info@francescadesalvia.com",
          telephone: "+34694461956",
          areaServed: { "@type": "AdministrativeArea", name: "Tenerife, Canary Islands" },
          sameAs: [EMBEDS.instagram, EMBEDS.facebook, EMBEDS.soundcloudProfile],
        },
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          name: "Shakti Love Music",
          url: SITE_URL,
          inLanguage: ["en", "es"],
        },
        {
          "@type": "WebPage",
          "@id": `${canonical}#webpage`,
          url: canonical,
          name: metadata.title,
          description: metadata.description,
          inLanguage: lang,
          about: { "@id": `${SITE_URL}/#shakti-love-music` },
        },
      ],
    };
    let script = document.getElementById("seo-structured-data");
    if (!script) {
      script = document.createElement("script");
      script.id = "seo-structured-data";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, [canonical, lang, metadata, path]);

  return null;
}